import { lazy, Suspense } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionDivider from './SectionDivider';

const PigeonMap = lazy(() => import('pigeon-maps').then(m => ({ default: m.Map })));
const PigeonMarker = lazy(() => import('pigeon-maps').then(m => ({ default: m.Marker })));

export default function MapSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'fullwidth';
    const accent = config.accent_color || primaryColor;

    const lat = content.latitude || '-12.0464';
    const lng = content.longitude || '-77.0428';
    const zoom = content.zoom || 15;
    const mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=${zoom}&output=embed`;

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    // Lite variant — pigeon-maps (OpenStreetMap, no API key)
    if (variant === 'lite') {
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && (
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ color: s.text }}>{content.title}</h2>
                    )}
                    <div className="rounded-xl overflow-hidden shadow-lg">
                        <Suspense fallback={<div style={{ height: 400, background: s.dark ? '#1f2937' : '#f3f4f6' }} className="flex items-center justify-center text-gray-400">Cargando mapa...</div>}>
                            <PigeonMap defaultCenter={[latNum, lngNum]} defaultZoom={zoom} height={400}>
                                <PigeonMarker anchor={[latNum, lngNum]} color={accent} />
                            </PigeonMap>
                        </Suspense>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" config={config}/>
            </section>
        );
    }

    // Fullwidth variant — just the map
    if (variant === 'fullwidth') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && (
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ color: s.text }}>{content.title}</h2>
                    )}
                    <div className="rounded-xl overflow-hidden shadow-lg" style={{ height: '450px' }}>
                        <iframe
                            src={mapSrc}
                            width="100%" height="100%"
                            style={{ border: 0 }}
                            allowFullScreen loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Ubicacion"
                        />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // With-info variant — map + info panel
    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && (
                    <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: s.text }}>{content.title}</h2>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Map */}
                    <div className="lg:col-span-2 rounded-xl overflow-hidden shadow-lg" style={{ minHeight: '400px' }}>
                        <iframe
                            src={mapSrc}
                            width="100%" height="100%"
                            style={{ border: 0, minHeight: '400px' }}
                            allowFullScreen loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Ubicacion"
                        />
                    </div>
                    {/* Info panel */}
                    <div className={`rounded-xl p-6 space-y-6 ${s.dark ? 'bg-white/10' : 'bg-gray-50'}`}>
                        {content.address && (
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                    <svg className="w-5 h-5" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold mb-1" style={{ color: s.text }}>Direccion</h4>
                                    <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{content.address}</p>
                                </div>
                            </div>
                        )}
                        {(brand?.contact_phone || content.phone) && (
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                    <svg className="w-5 h-5" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold mb-1" style={{ color: s.text }}>Telefono</h4>
                                    <p className="text-sm" style={{ color: s.textSub }}>{content.phone || brand?.contact_phone}</p>
                                </div>
                            </div>
                        )}
                        {content.hours && (
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                    <svg className="w-5 h-5" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold mb-1" style={{ color: s.text }}>Horario</h4>
                                    <p className="text-sm whitespace-pre-line" style={{ color: s.textSub }}>{content.hours}</p>
                                </div>
                            </div>
                        )}
                        {(brand?.contact_email || content.email) && (
                            <div className="flex gap-3">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                    <svg className="w-5 h-5" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold mb-1" style={{ color: s.text }}>Email</h4>
                                    <p className="text-sm" style={{ color: s.textSub }}>{content.email || brand?.contact_email}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}
